import { AfterViewInit, DestroyRef, Directive, ElementRef, input, OnDestroy, signal, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import { BaseTooltip } from '../abstract/base-tooltip';
import { SimpleTooltipComponent } from '../components/simple-tooltip/simple-tooltip.component';
import { TooltipPosition } from '../interfaces/tooltip.interface';
import { TooltipService } from '../services/tooltip.service';

/**
 * @description
 * Apply this directive to the element that will trigger a tooltip to be opened and from hereon will be called the "activator element".
 *
 * By default, opening a tooltip requires a click on the activator element.
 * Scrolling on the page closes the tooltip, as does clicking outside the tooltip or the activator element.
 *
 * @Input component - The component that will be displayed as tooltip. If none provided, "SimpleTooltipComponent" is used.
 * @Input componentData (required) - The data to be supplied to the component that will be displayed as tooltip.
 *   If a component wasn't provided, data should look like: `{ tooltipText: string }`.
 *
 * @Input trigger - Determines how the tooltip should be triggered. Defaults to `'click'`. Possible values:
 *   - `'click'`: Tooltip shows on click, hides on outside click/scroll.
 *   - `'hover'`: Tooltip shows on mouseenter, hides on mouseleave.
 *
 * @Input showDelay - Delay in milliseconds before showing the tooltip. Defaults to `0`.
 * @Input hideDelay - Delay in milliseconds before hiding the tooltip. Defaults to `0`.
 * @Input life - Maximum time (milliseconds) the tooltip will remain visible once shown.
 *   Defaults to infinite (`Infinity`). If set to a positive integer, the tooltip will hide automatically after that time.
 *
 * @Input position - Specifies the position of the tooltip relative to the activator element.
 *   Possible values: 'top', 'bottom', 'left', 'right', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight'. Defaults to 'right'.
 *
 * @example
 * <i appTooltip [component]="FieldChannelMappingsTooltipComponent" [componentData]="{ exportMappings: exportMappings }"></i>
 * @example
 * <i appTooltip [componentData]="{ tooltipText: text }"></i>
 */
@Directive({
    selector: '[appTooltip]',
    standalone: true,
})
export class TooltipDirective<T extends BaseTooltip> implements AfterViewInit, OnDestroy {
    component = input<Type<T>>(SimpleTooltipComponent as unknown as Type<T>);
    componentData = input.required<unknown>();

    /**
     * @description
     * Determines how the tooltip should be triggered.
     * Defaults to "click"; can be set to "hover".
     */
    trigger = input<'click' | 'hover'>('click');

    /**
     * Delay (ms) to show the tooltip after the trigger event.
     * Default = 0
     */
    showDelay = input<number>(0);

    /**
     * Delay (ms) to hide the tooltip after losing the trigger event.
     * Default = 0
     */
    hideDelay = input<number>(0);

    /**
     * Maximum time (ms) the tooltip will remain visible after it is shown.
     * Default = Infinity (never hides automatically).
     * If set to a positive integer, the tooltip will hide automatically after that time has elapsed.
     */
    life = input<number>(Infinity);

    /**
     * Specifies the position of the tooltip relative to the activator element.
     * Possible values: 'top', 'bottom', 'left', 'right', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight'. Defaults to 'right'.
     */
    position = input<TooltipPosition>('right');

    displayHtmlContent = input<boolean>(false);
    tooltipTextWidthCharacters = input<number>(120);

    closeOtherTooltips = input<boolean>(false);

    private showTimer: any;
    private hideTimer: any;
    private lifeTimer: any;
    private hoverListenersAttached = false;

    private isHoveringActivator = signal<boolean>(false);
    private isHoveringTooltip = signal<boolean>(false);

    constructor(
        private el: ElementRef,
        private tooltipService: TooltipService,
        private destroyRef: DestroyRef,
    ) {}

    ngAfterViewInit() {
        if (this.trigger() === 'click') {
            this.actOnClick();
        } else if (this.trigger() === 'hover') {
            this.actOnHover();
        }
    }

    private actOnClick() {
        fromEvent(this.el.nativeElement, 'click')
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1))
            .subscribe(() => {
                if (!this.tooltipService.isTooltipOpen()) {
                    this.clearAllTimers();
                    this.scheduleShow();
                } else {
                    this.clearAllTimers();
                    this.scheduleHide();
                }
            });
    }

    private actOnHover() {
        fromEvent(this.el.nativeElement, 'mouseenter')
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1))
            .subscribe(() => {
                this.isHoveringActivator.set(true);
                clearTimeout(this.hideTimer);
                this.scheduleShow();
            });

        fromEvent(this.el.nativeElement, 'mouseleave')
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1))
            .subscribe(() => {
                this.isHoveringActivator.set(false);
                clearTimeout(this.showTimer);
                if (this.tooltipService.isTooltipOpen() && !this.isHoveringTooltip()) {
                    this.scheduleHide();
                }
            });
    }

    ngOnDestroy(): void {
        this.clearAllTimers();
        this.onTooltipHidden();
    }

    private scheduleShow(): void {
        if (this.showDelay() > 0) {
            // Timeout OK
            this.showTimer = setTimeout(() => this.showTooltip(), this.showDelay());
        } else {
            this.showTooltip();
        }
    }

    private scheduleHide(): void {
        if (this.hideDelay() > 0) {
            // Timeout OK
            this.hideTimer = setTimeout(() => this.hideTooltip(), this.hideDelay());
        } else {
            this.hideTooltip();
        }
    }

    private showTooltip(): void {
        if (this.closeOtherTooltips() && this.tooltipService.isTooltipOpen()) {
            this.tooltipService.hideTooltip();
        }

        if (!this.tooltipService.isTooltipOpen()) {
            this.tooltipService.showTooltip(
                this.component(),
                this.el,
                this.componentData(),
                this.position(),
                this.displayHtmlContent(),
                this.tooltipTextWidthCharacters(),
                this.onTooltipShown.bind(this),
                this.onTooltipHidden.bind(this),
            );

            if (isFinite(this.life()) && this.life() > 0) {
                // Timeout OK
                this.lifeTimer = setTimeout(() => this.hideTooltip(), this.life());
            }

            this.attachTooltipHoverListeners();
        }
    }

    private hideTooltip(): void {
        this.tooltipService.hideTooltip();
        this.clearAllTimers();
    }

    private clearAllTimers(): void {
        clearTimeout(this.showTimer);
        clearTimeout(this.hideTimer);
        clearTimeout(this.lifeTimer);
    }

    private attachTooltipHoverListeners() {
        if (this.hoverListenersAttached) return;
        this.hoverListenersAttached = true;

        const tooltipEl = document.querySelector('.tooltipWrapper');
        if (!tooltipEl) return;

        fromEvent(tooltipEl, 'mouseenter')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.isHoveringTooltip.set(true);
                clearTimeout(this.hideTimer);
            });

        fromEvent(tooltipEl, 'mouseleave')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.isHoveringTooltip.set(false);

                if (!this.isHoveringActivator()) {
                    this.scheduleHide();
                }
            });
    }

    private onTooltipHidden() {
        this.el?.nativeElement?.removeAttribute('active');
    }

    private onTooltipShown() {
        this.el?.nativeElement?.setAttribute('active', '');
    }
}
